import React, { useState, useEffect, useRef } from "react";
import "./HallRentalForm.css";

function HallRentalForm() {
  const formRef = useRef(null);
  const successRef = useRef(null);
  const savedFormPayload = JSON.parse(localStorage.getItem("hallFormData"));
  const expiryDuration = 24 * 60 * 60 * 1000;
  let savedForm = null;

  if (savedFormPayload) {
    const { data, savedAt } = savedFormPayload;
    const now = new Date().getTime();
    const isExpired = now - savedAt > expiryDuration;

    if (!isExpired) {
      savedForm = data;
    } else {
      localStorage.removeItem("hallFormData");
      localStorage.removeItem("hallFormStep");
    }
  }

  const savedStep = parseInt(localStorage.getItem("hallFormStep")) || 1;
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState(savedStep);
  const [formData, setFormData] = useState(
    savedForm || {
      fullName: "",
      streetAddress: "",
      city: "",
      postcode: "",
      country: "",
      contactNumber: "",
      email: "",
      eventType: "",
      eventDate: "",
      startTime: "",
      endTime: "",
      guests: "",
      specialRequirements: "",
      agreedTerms: false,
      signature: "",
      date: new Date().toISOString().split("T")[0],
      supportingDocs: null,
    }
  );

  const [submitted, setSubmitted] = useState(false);

  // --- Persist data and step changes ---
  useEffect(() => {
    const payload = {
      data: formData,
      savedAt: new Date().getTime(), // Timestamp for expiry tracking
    };
    localStorage.setItem("hallFormData", JSON.stringify(payload));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem("hallFormStep", step);
  }, [step]);

  const getScrollOffset = () => {
    const width = window.innerWidth;
    if (width < 600) return -220;
    if (width < 1024) return -180;
    return -70;
  };

  const scrollToForm = (behavior = "smooth") => {
    if (!formRef.current) return;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const rect = formRef.current.getBoundingClientRect();
        const scrollOffset = getScrollOffset();
        const top = rect.top + window.scrollY - scrollOffset;
        const scrollTarget = Math.max(0, Math.floor(top));
        window.scrollTo({ top: scrollTarget, behavior });
      })
    );
  };

  const goToStep = (newStep) => {
    setStep(newStep);
    scrollToForm("smooth");
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    // ✅ Handle file uploads
    if (type === "file") {
      const file = files[0];
      if (!file) return;

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        alert("Invalid file type. Only PDF, JPG, or PNG files are allowed.");
        e.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        alert("File size must be less than 10MB.");
        e.target.value = "";
        return;
      }

      setFormData({ ...formData, [name]: file });
      return;
    }

    // Handle time fields
    if (name === "startTime" || name === "endTime") {
      const start = name === "startTime" ? value : formData.startTime;
      const end = name === "endTime" ? value : formData.endTime;

      if (start && end) {
        if (start === end) {
          alert("Start and end time cannot be the same.");
          return;
        }
        // Overnight events are allowed, so no other checks needed
      }

      setFormData({ ...formData, [name]: value });
      return;
    }

    // Handle checkboxes & other inputs
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const nextStep = () => {
    const stepFields = {
      1: [
        "fullName",
        "contactNumber",
        "email",
        "country",
        "streetAddress",
        "city",
        "postcode",
      ],
      2: ["eventType", "eventDate", "startTime", "endTime", "guests"],
      3: ["agreedTerms"],
    };

    const required = stepFields[step] || [];

    const missing = required.filter((field) => {
      const value = formData[field];
      if (typeof value === "boolean" || value instanceof File) return false;
      return !value || (typeof value === "string" && value.trim() === "");
    });

    if (step === 3 && !formData.agreedTerms) {
      alert("Please agree to the Terms and Conditions before continuing.");
      return;
    }

    if (missing.length > 0) {
      alert("Please complete all required fields before continuing.");
      return;
    }

    if (step < 4) goToStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) goToStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreedTerms) {
      alert("Please agree to the Terms and Conditions before submitting.");
      return;
    }

    if (
      formData.signature.trim().toLowerCase() !==
      formData.fullName.trim().toLowerCase()
    ) {
      alert(
        "The signature must match the full name entered in the Applicant Information section."
      );
      return;
    }

    const payload = new FormData();

    // Append all fields
    Object.keys(formData).forEach((key) => {
      if (key === "supportingDocs" && formData.supportingDocs) {
        payload.append("supportingDocs", formData.supportingDocs);
      } else {
        payload.append(key, formData[key]);
      }
    });

    // --- inside handleSubmit ---
    try {
      setLoading(true);

      const response = await fetch("/php/save-hall-rental.php", {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (result.success) {
        localStorage.removeItem("hallFormData");
        localStorage.removeItem("hallFormStep");
        setSubmitted(true);

        setTimeout(() => {
          if (successRef.current) {
            const rect = successRef.current.getBoundingClientRect();
            const scrollOffset = getScrollOffset();
            const top = rect.top + window.scrollY - scrollOffset;
            window.scrollTo({ top, behavior: "smooth" });
          }
        }, 400);
      } else {
        alert("Submission failed: " + result.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong while submitting the form.");
    } finally {
      setLoading(false);
    }
  };

  const Required = () => <span className="hall-required">*</span>;

  const SpinnerOverlay = () => (
    <div className="spinner-overlay">
      <div className="spinner"></div>
    </div>
  );

  if (submitted) {
    return (
      <div className="hall-success-message" ref={successRef}>
        <div className="hall-success-icon">✓</div>
        <h2>Application Submitted Successfully!</h2>
        <p className="hall-success-text">
          Thank you for completing your hall rental application. <br />
          Our booking team will contact you shortly to confirm your event
          details and availability.
        </p>
        <button
          type="button"
          className="hall-success-back-btn post-review-btn"
          onClick={() => {
            setSubmitted(false);
            setStep(1);
            scrollToForm("smooth");
            setFormData({
              fullName: "",
              streetAddress: "",
              city: "",
              postcode: "",
              country: "",
              contactNumber: "",
              email: "",
              eventType: "",
              eventDate: "",
              startTime: "",
              endTime: "",
              guests: "",
              specialRequirements: "",
              agreedTerms: false,
              signature: "",
              date: new Date().toISOString().split("T")[0],
              supportingDocs: null,
            });
            localStorage.removeItem("hallFormData");
            localStorage.removeItem("hallFormStep");
          }}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <form className="hall-form-container" onSubmit={handleSubmit} ref={formRef}>
      {loading && <SpinnerOverlay />}
      <h2 className="hall-form-title">Lasgidi Hall Rental Application</h2>

      <p className="hall-intro-text">
        Please complete the form below to request a hall rental at Lasgidi
        Restaurant. Ensure all information is accurate and review our terms
        before submission.
        <br />
        <br />
        Once received, our events team will reach out to confirm availability
        and guide you through the next steps.
      </p>

      {/* PROGRESS BAR */}
      <div className="hall-progress-bar">
        {[1, 2, 3, 4].map((num) => (
          <div
            key={num}
            className={`hall-progress-step ${step >= num ? "active" : ""} ${
              num < step ? "clickable" : ""
            }`}
            onClick={() => {
              if (num < step) setStep(num); // Allow backward jump only
            }}
          ></div>
        ))}
      </div>

      <div className="hall-steps-container">
        {/* STEP 1: Applicant Information */}
        {step === 1 && (
          <div className="hall-form-step fade-in">
            <h3 className="hall-section-title">Applicant Information</h3>

            <div className="hall-input-row">
              <div className="hall-input-field">
                <label>
                  Full Name <Required />
                </label>

                <input
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="hall-input-field">
                <label>
                  Contact Number <Required />
                </label>
                <input
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="hall-input-row">
              <div className="hall-input-field">
                <label>
                  Email <Required />
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="hall-input-field">
                <label>
                  Country <Required />
                </label>
                <input
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <label>
              Street Address <Required />
            </label>
            <input
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleChange}
              required
            />

            <div className="hall-input-row">
              <div className="hall-input-field">
                <label>
                  City <Required />
                </label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="hall-input-field">
                <label>
                  Postcode <Required />
                </label>
                <input
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="hall-navigation">
              <button
                type="button"
                onClick={nextStep}
                className="hall-btn next post-review-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Event Details */}
        {step === 2 && (
          <div className="hall-form-step fade-in">
            <h3 className="hall-section-title">Event Details</h3>

            <div className="hall-input-row">
              <div className="hall-input-field">
                <label>
                  Event Type <Required />
                </label>
                <input
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  placeholder="e.g. Wedding, Seminar, Birthday"
                  required
                />
              </div>
              <div className="hall-input-field">
                <label>
                  Date of Event <Required />
                </label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
            </div>

            <div className="hall-input-row">
              <div className="hall-input-field">
                <label>
                  Start Time <Required />
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="hall-input-field">
                <label>
                  End Time <Required />
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="hall-input-field">
              <label>
                Estimated Number of Guests <Required />
              </label>
              <input
                name="guests"
                value={formData.guests}
                onChange={handleChange}
                required
              />
            </div>

            <label>Special Requirements</label>
            <textarea
              name="specialRequirements"
              value={formData.specialRequirements}
              onChange={handleChange}
              placeholder="Bring your food or bring your alcohol or both; accessibility"
            />

            {/* FILE UPLOAD FIELD */}
            <div className="hall-upload-wrapper">
              <label className="hall-upload-label">
                Upload Supporting Document
              </label>
              <div className="hall-upload-box">
                <input
                  type="file"
                  id="supportingDocs"
                  name="supportingDocs"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.png"
                />
              </div>
              <p className="hall-upload-note">
                Accepted formats: PDF, DOC, JPG, PNG (max 10MB)
              </p>
            </div>

            <div className="hall-navigation">
              <button
                type="button"
                onClick={prevStep}
                className="hall-btn back post-review-btn"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="hall-btn next post-review-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Terms & Conditions */}
        {step === 3 && (
          <div className="hall-form-step fade-in">
            <h3 className="hall-section-title">Terms & Conditions</h3>

            <div className="hall-terms-box">
              <p>
                <strong>Booking Policy:</strong> Bookings are subject to
                availability. A 50% deposit is required to secure your
                reservation and the balance must be paid two weeks before the
                event date. For bookings made less than two weeks before the
                event, full payment is required.
              </p>

              <p>
                <strong>Use of Facilities:</strong> Events must finish by
                11:30pm, with cleaning allowed until midnight. Weekend hours may
                be extended by prior agreement. The hall may only be used for
                the purpose stated in this application; any change must be
                approved.
              </p>

              <p>
                <strong>Noise:</strong> The applicant is responsible for
                ensuring that noise levels remain within regulatory limits.
                Music must stop by 11:30pm on weekdays; weekend hours may be
                extended by agreement.
              </p>

              <p>
                <strong>Liability:</strong> The applicant is responsible for any
                damage to the hall, its equipment, or facilities during the
                rental period. A refundable security deposit (£100–£200) is
                required depending on the event type.
              </p>

              <p>
                <strong>Fees:</strong> The hall rental fee is £700 per day or
                £100 per hour.
              </p>

              <p>
                <strong>Cleanliness:</strong> The hall and car park must be left
                in the same condition as found. Additional cleaning charges may
                apply if this condition is not met.
              </p>

              <p>
                <strong>Music Equipment:</strong> Music equipment can be hired
                at £200 per event.
              </p>

              <p>
                <strong>Cancellations:</strong> Notice of cancellation must be
                given at least 21 days before the event to receive a refund.
                Cancellations within 14 days may be subject to partial or no
                refund.
              </p>

              <p>
                <strong>Insurance:</strong> Proof of public liability insurance
                may be required depending on the event.
              </p>

              <p>
                <strong>Alcohol and Smoking:</strong> Alcohol may only be served
                with appropriate licensing. Smoking is strictly prohibited
                within the hall premises.
              </p>

              <p>
                <strong>Decorations:</strong> Decorations must be approved
                before use and must not damage the property. Nails, staples,
                adhesives, confetti, and live candles are not permitted.
                Decorations cannot be done the day before and all items must be
                removed on the same day.
              </p>

              <p>
                <strong>Security:</strong> Security personnel must be present to
                control the car park and maintain order. The cost will be
                charged to the client or they may provide their own security.
              </p>

              <p>
                <strong>Children:</strong> Children must be supervised at all
                times. Keep them within the designated hall (downstairs or
                upstairs) as rented. They must not play near the main road
                unsupervised.
              </p>

              <p>
                <strong>Indemnification:</strong> The applicant agrees to
                indemnify and hold harmless the hall and its owners/operators
                from any claim, loss, or liability arising from use of the
                premises.
              </p>
            </div>

            <label className="hall-checkbox">
              <input
                type="checkbox"
                name="agreedTerms"
                checked={formData.agreedTerms}
                onChange={handleChange}
              />
              I have read and agree to the Terms and Conditions <Required />
            </label>

            <div className="hall-navigation">
              <button
                type="button"
                onClick={prevStep}
                className="hall-btn back post-review-btn"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="hall-btn next post-review-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Privacy & Signature */}
        {step === 4 && (
          <div className="hall-form-step fade-in">
            <h3 className="hall-section-title">Privacy Notice</h3>
            <div className="hall-privacy-box">
              <p>
                Your personal data will be used solely for processing your hall
                rental application and managing your booking. Your information
                will not be shared with third parties without your consent,
                except as required by law.
              </p>
            </div>

            <label>
              Your Signature (type your full name) <Required />
            </label>
            <input
              name="signature"
              value={formData.signature}
              onChange={handleChange}
              required
            />

            <label>
              Date <Required />
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              readOnly
            />

            <div className="hall-navigation">
              <button
                type="button"
                onClick={prevStep}
                className="hall-btn back post-review-btn"
              >
                Back
              </button>
              <button type="submit" className="hall-btn submit post-review-btn">
                Submit Application
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

export default HallRentalForm;
